import {
  useCallback, useEffect, useMemo, useRef,
} from 'react';
import { toast } from 'react-toastify';
import { VALUE_CHAIN_STATUS } from 'constants/interfaces';
import { VALUE_CHAIN_PROCESSING_CHECK_INTERVAL } from 'constants/valueChain';
import i18n from 'i18n';
import { useAppDispatch, useAppSelector } from 'store/hooks/useApp';
import selectProcessingValuesChainMapId from 'store/slices/localStorage/selectors';
import { removeProcessingValuesChainMapId } from 'store/slices/localStorage/slice';
import { useLazyCheckValueChainMapStatusQuery } from 'store/slices/valueChainMap/apis/valueChainMapApi';

const useValueChainProcessingSync = () => {
  const dispatch = useAppDispatch();
  const processingValuesChainMapId = useAppSelector(selectProcessingValuesChainMapId);
  const pendingChecksRef = useRef<Set<number>>(new Set());
  const [checkValueChainMapStatus] = useLazyCheckValueChainMapStatusQuery();

  const processingIds = useMemo(
    () => Array.from(new Set(processingValuesChainMapId)),
    [processingValuesChainMapId],
  );

  const handleCheckProcessingValueChain = useCallback((id: number) => {
    if (pendingChecksRef.current.has(id)) {
      return;
    }

    pendingChecksRef.current.add(id);

    checkValueChainMapStatus({ chainId: `${id}` })
      .unwrap()
      .then(({ data: updatedItem }) => {
        if (updatedItem.status === VALUE_CHAIN_STATUS.processing) {
          return;
        }

        toast.success(i18n.t('notifications.valueChainMapNameHasBeenCreated', { name: updatedItem.name }));
        dispatch(removeProcessingValuesChainMapId(id));
      })
      .catch(() => {
        dispatch(removeProcessingValuesChainMapId(id));
      })
      .finally(() => {
        pendingChecksRef.current.delete(id);
      });
  }, [checkValueChainMapStatus, dispatch]);

  useEffect(() => {
    if (processingIds.length === 0) {
      return undefined;
    }

    processingIds.forEach(handleCheckProcessingValueChain);

    const intervalId = window.setInterval(() => {
      processingIds.forEach(handleCheckProcessingValueChain);
    }, VALUE_CHAIN_PROCESSING_CHECK_INTERVAL);

    return () => window.clearInterval(intervalId);
  }, [handleCheckProcessingValueChain, processingIds]);
};

export default useValueChainProcessingSync;
